import React, { useMemo, useState } from 'react';

const detectType = (fileInfo) => {
  const type = (fileInfo?.file_type || '').toLowerCase();
  const name = (fileInfo?.original_filename || '').toLowerCase();
  if (!type && !name) return 'unknown';
  if (type.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(name)) return 'image';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return 'other';
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleString();
};

const RepliesModal = ({ isOpen, onClose, replies = [] }) => {
  const [zoomById, setZoomById] = useState({});

  const hasAny = useMemo(() => (Array.isArray(replies) && replies.length > 0), [replies]);

  if (!isOpen) return null;

  const handleZoom = (id, delta) => {
    setZoomById(prev => {
      const current = prev[id] ?? 1;
      const next = Math.max(0.25, Math.min(4, current + delta));
      return { ...prev, [id]: next };
    });
  };

  const handleReset = (id) => {
    setZoomById(prev => ({ ...prev, [id]: 1 }));
  };

  const handlePrint = (url) => {
    if (!url) return;
    const win = window.open(url, '_blank');
    if (!win) return;
    const triggerPrint = () => {
      try { win.focus(); win.print(); } catch (_) {}
    };
    // Some browsers need a delay for content to load
    setTimeout(triggerPrint, 800);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      padding: '1.5rem',
    }}>
      <div style={{
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        width: '100%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-lg)'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h3 style={{ margin: 0 }}>Admin Replies</h3>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {hasAny ? `${replies.length} replies` : 'No replies yet'}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)',
            fontSize: '1.25rem', cursor: 'pointer'
          }}>×</button>
        </div>

        <div style={{ padding: '1rem 1.5rem', overflow: 'auto' }}>
          {!hasAny ? (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', opacity: 0.5, marginBottom: '0.5rem' }}>💬</div>
                <div style={{ color: 'var(--text-secondary)' }}>No replies yet</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {replies.map((r) => {
                const id = r._id || `${r.admin_email}-${r.created_at}`;
                const type = detectType(r.file_info);
                const zoom = zoomById[id] ?? 1;
                const url = r?.file_info?.url;
                return (
                  <div key={id} className="card">
                    <div className="card-body" style={{ display: 'grid', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{r.admin_email || 'Admin'}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{formatDate(r.created_at)}</div>
                        </div>
                        {url && (
                          <div className="d-flex" style={{ gap: '0.5rem' }}>
                            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => handleZoom(id, 0.25)}>Zoom In</button>
                            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => handleZoom(id, -0.25)}>Zoom Out</button>
                            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => handleReset(id)}>Reset</button>
                            <button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => handlePrint(url)}>Print</button>
                          </div>
                        )}
                      </div>
                      {r.message && (
                        <div style={{ color: 'var(--text-primary)' }}>{r.message}</div>
                      )}

                      {url && (
                        type === 'image' ? (
                          <div style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--border-radius)',
                            padding: '0.5rem',
                            background: 'var(--bg-secondary)',
                            overflow: 'auto'
                          }}>
                            <img
                              src={url}
                              alt={r.file_info?.original_filename || 'reply-attachment'}
                              style={{
                                transform: `scale(${zoom})`,
                                transformOrigin: 'top left',
                                maxWidth: '100%',
                                display: 'block'
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        ) : type === 'pdf' ? (
                          <div style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--border-radius)',
                            background: 'var(--bg-secondary)',
                            overflow: 'auto',
                            height: '480px',
                            position: 'relative'
                          }}>
                            <div style={{ width: `${zoom * 100}%`, height: '100%', transformOrigin: 'top left' }}>
                              <iframe
                                src={url}
                                title={r.file_info?.original_filename || 'reply-pdf'}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <a className="btn btn-secondary" href={url} target="_blank" rel="noopener noreferrer">Open Attachment</a>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepliesModal;


